/**
 * 项目配置页面 - 第一步
 * 包含：项目名称、投标主体、项目类型、字数设定、模型选择、文件上传
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CloudArrowUpIcon, DocumentTextIcon, ArrowPathIcon, TrashIcon, FolderIcon, PlusIcon, DocumentIcon, XMarkIcon, TableCellsIcon, PresentationChartBarIcon, DocumentDuplicateIcon, PhotoIcon, ArrowRightIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { ProjectConfig, CompanyInfo, ProjectType } from '../types';
import { getModelDisplayName, filterModels, TEXT_MODELS_CONFIG } from '../config/aiConfig';
import { documentApi, companyApi, authApi, projectApi, projectGroupApi, configApi } from '../services/api'; // 添加 projectApi, projectGroupApi
import { draftStorage } from '../utils/draftStorage';
import Toast, { ToastType } from '../components/Toast';
import axios from 'axios';

interface ProjectSetupProps {
  projectConfig: ProjectConfig;
  fileContent: string;
  projectId?: string;
  onProjectConfigChange: (config: ProjectConfig) => void;
  onFileUpload: (content: string) => void;
  onNext?: () => void;
}

const PROJECT_TYPES: ProjectType[] = ['通用类', '数据类', '定开类', '产品类', '两轮车'];

const ProjectSetup: React.FC<ProjectSetupProps> = ({
  projectConfig,
  fileContent,
  projectId,
  onProjectConfigChange,
  onFileUpload,
  onNext,
}) => {
  const navigate = useNavigate();
  
  // 招标文件上传状态（只能上传一个）
  const [, setTenderFile] = useState<File | null>(null);
  const [tenderFileInfo, setTenderFileInfo] = useState<{id?: string; name: string; size: number; uploadedAt: string} | null>(null);
  const [uploadingTender, setUploadingTender] = useState(false);
  const tenderFileInputRef = useRef<HTMLInputElement>(null);
  
  // 其他资料上传状态（可上传多个）
  const [, setOtherFiles] = useState<File[]>([]);
  const [otherFilesInfo, setOtherFilesInfo] = useState<Array<{id: string; name: string; size: number; uploadedAt: string}>>([]);
  const [uploadingOther, setUploadingOther] = useState(false);
  const otherFilesInputRef = useRef<HTMLInputElement>(null);
  
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Toast 通知状态
  const [toast, setToast] = useState<{
    show: boolean;
    type: ToastType;
    title: string;
    messages?: string[];
  }>({
    show: false,
    type: 'info',
    title: '',
    messages: [],
  });

  const showToast = (type: ToastType, title: string, messages?: string[]) => {
    setToast({ show: true, type, title, messages });
  };

  const closeToast = () => {
    setToast(prev => ({ ...prev, show: false }));
  };
  
  // 本地状态管理
  const [localConfig, setLocalConfig] = useState<ProjectConfig>(projectConfig);
  const [showNewCompanyModal, setShowNewCompanyModal] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newCompanyFullName, setNewCompanyFullName] = useState('');
  const [companies, setCompanies] = useState<CompanyInfo[]>([]); // 修改：初始为空，从 API 加载
  const [loadingCompanies, setLoadingCompanies] = useState(false); // 新增：加载状态
  const [projectGroups, setProjectGroups] = useState<{id: string; name: string; is_default: boolean}[]>([]);

  // 大模型相关状态
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [hasAutoFetched, setHasAutoFetched] = useState(false); // 标记是否已自动获取过
  const [userDefaultModel, setUserDefaultModel] = useState<string>(''); // 用户设置的默认文本模型
  
  // 同步 projectConfig 到 localConfig（当打开已保存的项目时）
  useEffect(() => {
    setLocalConfig(projectConfig);
  }, [projectConfig]);
  
  // 加载公司列表
  useEffect(() => {
    async function loadCompanies() {
      try {
        setLoadingCompanies(true);
        const response = await companyApi.getCompanies();
        const companyList = response.data.map((c: any) => ({
          id: c.unified_social_credit_code,  // 使用统一社会信用代码作为 id
          name: c.name,
          fullName: c.full_name || c.name,
        }));
        setCompanies(companyList);
      } catch (error) {
        console.error('加载公司列表失败:', error);
        showToast('error', '加载公司失败', ['请检查网络连接']);
      } finally {
        setLoadingCompanies(false);
      }
    }
    
    loadCompanies();

    // 加载项目组列表
    projectGroupApi.getSimpleList().then(res => {
      const groups = res.data || [];
      setProjectGroups(groups);
      // 如果当前未选任何项目组，自动选中默认项目组
      setLocalConfig(prev => {
        if (!prev.groupId && groups.length > 0) {
          const defaultGroup = groups.find((g: any) => g.is_default) || groups[0];
          if (defaultGroup) {
            const newConfig = { ...prev, groupId: defaultGroup.id };
            onProjectConfigChange(newConfig);
            return newConfig;
          }
        }
        return prev;
      });
    }).catch(() => {});
  }, [onProjectConfigChange]); // eslint-disable-line react-hooks/exhaustive-deps

  // 计算页数（假设每页约500字）
  const calculatePages = (words: number): number => {
    return Math.ceil(words / 500);
  };

  // 格式化报价显示（添加千分位分隔符）
  const formatPrice = (price: number): string => {
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // 更新本地配置并同步到父组件
  const updateConfig = async (updates: Partial<ProjectConfig>) => {
    const newConfig = { ...localConfig, ...updates };
    setLocalConfig(newConfig);
    onProjectConfigChange(newConfig);
    
    // 如果已经有项目ID，并且更新了字数配置，立即保存到数据库
    if (projectId && 'wordCount' in updates) {
      try {
        await projectApi.updateProject(projectId, {
          word_count: updates.wordCount
        });
        console.log('字数配置已自动保存到数据库:', updates.wordCount);
      } catch (error) {
        console.error('自动保存字数配置失败:', error);
        // 静默失败，不打扰用户体验
      }
    }
  };

  // 获取可用模型列表
  const fetchAvailableModels = useCallback(async () => {
    setLoadingModels(true);
    setMessage(null);
    
    try {
      const response = await configApi.getModels();
      if (response.data.success && response.data.models) {
        setAvailableModels(response.data.models);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: '获取模型列表失败，请检查网络连接' });
      console.error('获取模型失败:', error);
    } finally {
      setLoadingModels(false);
    }
  }, []);

  // 加载用户设置的默认文本模型
  useEffect(() => {
    async function loadUserDefaultModel() {
      try {
        const response = await authApi.getSettings();
        if (response.data && response.data.text_model) {
          setUserDefaultModel(response.data.text_model);
        }
      } catch (error) {
        console.error('加载用户默认模型失败:', error);
      }
    }
    loadUserDefaultModel();
  }, []);

  // 组件挂载时，如果模型列表为空，自动刷新一次
  useEffect(() => {
    if (availableModels.length === 0 && !hasAutoFetched && !loadingModels) {
      setHasAutoFetched(true);
      fetchAvailableModels();
    }
  }, [availableModels.length, hasAutoFetched, loadingModels, fetchAvailableModels]);

  // 当模型列表加载完成且用户有默认模型时，自动选择
  useEffect(() => {
    if (availableModels.length > 0 && userDefaultModel && !localConfig.selectedModel) {
      // 检查用户默认模型是否在可用列表中
      if (availableModels.includes(userDefaultModel)) {
        updateConfig({ selectedModel: userDefaultModel });
      }
    }
  }, [availableModels, userDefaultModel, localConfig.selectedModel]);

  // 加载已上传的文件列表
  useEffect(() => {
    const loadProjectFiles = async () => {
      if (!projectId) return;
      
      try {
        const response = await documentApi.getProjectFiles(projectId);
        
        if (response.data && response.data.files) {
          const files = response.data.files;
          
          // 分类文件
          const tender = files.find((f: any) => f.file_category === 'tender');
          const others = files.filter((f: any) => f.file_category === 'other');
          
          // 设置招标文件
          if (tender) {
            setTenderFileInfo({
              id: tender.id,
              name: tender.file_name,
              size: tender.file_size || 0,
              uploadedAt: tender.uploaded_at
            });
          }
          
          // 设置其他资料
          if (others.length > 0) {
            setOtherFilesInfo(others.map((f: any) => ({
              id: f.id,
              name: f.file_name,
              size: f.file_size || 0,
              uploadedAt: f.uploaded_at
            })));
          }
        }
      } catch (error) {
        console.error('加载项目文件失败:', error);
      }
    };
    
    loadProjectFiles();
  }, [projectId]);

  // 处理文件选择
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // 兼容旧逻辑：将旧的上传重定向到招标文件上传
      setTenderFile(file);
      handleTenderFileUpload(file);
    }
    event.target.value = '';
  };

  // 处理文件上传（兼容旧逻辑）
  const handleFileUpload = async (file: File) => {
    // 重定向到新的招标文件上传函数
    return handleTenderFileUpload(file);
  };

  // ==================== 招标文件上传处理 ====================
  // 处理招标文件选择（单文件）
  const handleTenderFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setTenderFile(file);
      handleTenderFileUpload(file);
    }
    // 清空input以便可以再次选择同一文件
    event.target.value = '';
  };

  // 上传招标文件
  const handleTenderFileUpload = async (file: File) => {
    try {
      setUploadingTender(true);
      setMessage(null);

      const response = await documentApi.uploadFile(file, 'tender', projectId);
      
      if (response.data.success) {
        setTenderFileInfo({
          id: response.data.file_id,
          name: file.name,
          size: file.size,
          uploadedAt: new Date().toISOString()
        });
        if (response.data.file_content) {
          draftStorage.clearAll();
          onFileUpload(response.data.file_content);
        }
        showToast('success', '招标文件上传成功！', [
          `文件名：${file.name}`,
          `大小：${(file.size / 1024 / 1024).toFixed(2)} MB`
        ]);
      } else {
        showToast('error', '招标文件上传失败', [response.data.message]);
        setTenderFile(null);
      }
    } catch (error: any) {
      showToast('error', '招标文件上传失败', [
        error.response?.data?.detail || '请检查网络连接后重试'
      ]);
      setTenderFile(null);
    } finally {
      setUploadingTender(false);
    }
  };

  // 删除招标文件
  const handleDeleteTenderFile = async () => {
    try {
      if (projectId && tenderFileInfo?.id) {
        await documentApi.deleteProjectFile(projectId, String(tenderFileInfo.id));
      }
      setTenderFile(null);
      setTenderFileInfo(null);
      showToast('info', '已删除招标文件');
    } catch (error: any) {
      showToast('error', '删除失败', [
        error.response?.data?.detail || '请检查网络连接后重试'
      ]);
    }
  };

  // ==================== 其他资料上传处理 ====================
  // 处理其他资料选择（多文件）
  const handleOtherFilesSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      setOtherFiles(prev => [...prev, ...newFiles]);
      handleOtherFilesUpload(newFiles);
    }
    // 清空input以便可以再次选择同一文件
    event.target.value = '';
  };

  // 上传其他资料
  const handleOtherFilesUpload = async (files: File[]) => {
    try {
      setUploadingOther(true);
      
      for (const file of files) {
        const response = await documentApi.uploadFile(file, 'other', projectId);
        
        if (response.data.success) {
          const newFileInfo = {
            id: response.data.file_id || String(Date.now()),
            name: file.name,
            size: file.size,
            uploadedAt: new Date().toISOString()
          };
          setOtherFilesInfo(prev => [...prev, newFileInfo]);
        } else {
          showToast('error', `文件 ${file.name} 上传失败`, [response.data.message]);
        }
      }
      
      if (files.length > 0) {
        showToast('success', '资料上传完成', [
          `成功上传 ${files.length} 个文件`
        ]);
      }
    } catch (error: any) {
      showToast('error', '资料上传失败', [
        error.response?.data?.detail || '请检查网络连接后重试'
      ]);
    } finally {
      setUploadingOther(false);
    }
  };

  // 删除其他资料
  const handleDeleteOtherFile = async (index: number) => {
    const file = otherFilesInfo[index];
    if (!file) return;
    
    try {
      if (projectId && file.id) {
        await documentApi.deleteProjectFile(projectId, String(file.id));
      }
      setOtherFiles(prev => prev.filter((_, i) => i !== index));
      setOtherFilesInfo(prev => prev.filter((_, i) => i !== index));
      showToast('info', '已删除资料文件');
    } catch (error: any) {
      showToast('error', '删除失败', [
        error.response?.data?.detail || '请检查网络连接后重试'
      ]);
    }
  };

  // 格式化文件大小
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  // 根据文件名获取文件类型和图标
  const getFileTypeInfo = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    
    switch (ext) {
      case 'pdf':
        return {
          icon: DocumentDuplicateIcon,
          bgColor: 'bg-red-100',
          iconColor: 'text-red-600',
          label: 'PDF'
        };
      case 'docx':
      case 'doc':
        return {
          icon: DocumentTextIcon,
          bgColor: 'bg-blue-100',
          iconColor: 'text-blue-600',
          label: 'Word'
        };
      case 'xlsx':
      case 'xls':
        return {
          icon: TableCellsIcon,
          bgColor: 'bg-green-100',
          iconColor: 'text-green-600',
          label: 'Excel'
        };
      case 'pptx':
      case 'ppt':
        return {
          icon: PresentationChartBarIcon,
          bgColor: 'bg-orange-100',
          iconColor: 'text-orange-600',
          label: 'PPT'
        };
      case 'txt':
        return {
          icon: DocumentIcon,
          bgColor: 'bg-gray-100',
          iconColor: 'text-gray-600',
          label: 'Text'
        };
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'webp':
        return {
          icon: PhotoIcon,
          bgColor: 'bg-purple-100',
          iconColor: 'text-purple-600',
          label: 'Image'
        };
      default:
        return {
          icon: DocumentIcon,
          bgColor: 'bg-gray-100',
          iconColor: 'text-gray-600',
          label: 'File'
        };
    }
  };

  // 添加新公司
  const handleAddCompany = () => {
    if (!newCompanyName.trim() || !newCompanyFullName.trim()) {
      setMessage({ type: 'error', text: '请填写公司简称和全称' });
      return;
    }

    const newCompany: CompanyInfo = {
      id: `custom_${Date.now()}`,
      name: newCompanyName,
      fullName: newCompanyFullName,
    };

    setCompanies([...companies, newCompany]);
    updateConfig({ company: newCompany });
    setNewCompanyName('');
    setNewCompanyFullName('');
    setShowNewCompanyModal(false);
    setMessage({ type: 'success', text: '公司添加成功' });
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Toast 通知 */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        messages={toast.messages}
        onClose={closeToast}
        duration={5000}
      />

      {/* 页面标题 */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white mb-4">
        <h1 className="text-2xl font-bold mb-2">📋 项目配置</h1>
        <p className="text-blue-100">请完善项目基础信息并上传招标文件，信息将用于标书生成，务必准确规范</p>
      </div>

      {/* 项目基本信息 */}
      <div className="bg-white rounded-lg shadow p-6 mb-4">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
          <DocumentTextIcon className="h-6 w-6 mr-2 text-blue-600" />
          项目基本信息
        </h2>

        <div className="space-y-6">
          {/* 第一行：项目别名 + 投标主体 */}
          <div className="grid grid-cols-2 gap-4">
            {/* 左侧：项目别名 */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100">
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                🏷️ 项目别名
              </label>
              <input
                type="text"
                value={localConfig.alias || ''}
                onChange={(e) => updateConfig({ alias: e.target.value })}
                placeholder="请输入项目别名（用于显示）"
                className="w-full px-3 py-2.5 bg-white border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
              />
            </div>

            {/* 右侧：投标主体 */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                🏢 投标主体 <span className="text-red-500">*</span>
              </label>
              <select
                value={localConfig.company?.id || ''}
                onChange={(e) => {
                  const creditCode = e.target.value;
                  const company = creditCode ? companies.find(c => c.id === creditCode) || null : null;
                  updateConfig({ company: company });
                }}
                className="w-full px-3 py-2.5 bg-white border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="">请选择投标主体</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name} - {company.fullName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 第二行：项目组 + 模型选择 */}
          <div className="grid grid-cols-2 gap-4">
            {/* 左侧：项目组 */}
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-4 border border-teal-100">
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                👥 项目组
              </label>
              <select
                value={localConfig.groupId || ''}
                onChange={(e) => updateConfig({ groupId: e.target.value || undefined })}
                className="w-full px-3 py-2.5 bg-white border border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
              >
                <option value="">请选择项目组</option>
                {[...projectGroups]
                  .sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0))
                  .map(g => (
                    <option key={g.id} value={g.id}>
                      {g.name}{g.is_default ? '（默认）' : ''}
                    </option>
                  ))}
              </select>
            </div>

            {/* 右侧：模型选择 */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold text-gray-800">
                  🤖 模型选择 <span className="text-red-500">*</span>
                </label>
                <button
                  onClick={fetchAvailableModels}
                  disabled={loadingModels}
                  className="p-1.5 text-purple-600 hover:text-purple-700 hover:bg-purple-100 rounded-md transition-colors disabled:opacity-50"
                  title="刷新可用模型"
                >
                  <ArrowPathIcon className={`w-4 h-4 ${loadingModels ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <select
                value={localConfig.selectedModel}
                onChange={(e) => updateConfig({ selectedModel: e.target.value })}
                className="w-full px-3 py-2.5 bg-white border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                disabled={availableModels.length === 0 && loadingModels}
              >
                <option value="">请选择模型</option>
                {TEXT_MODELS_CONFIG.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.company})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 标书字数 - 功能卡片 */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-gray-800">
                📝 标书字数
              </label>
              <div className="text-right">
                <span className="text-sm font-semibold text-green-500">
                  {(localConfig.wordCount / 10000).toFixed(1)}万字
                </span>
                <span className="text-xs text-gray-500 ml-1">
                  （约{calculatePages(localConfig.wordCount)}页）
                </span>
              </div>
            </div>
            <div className="space-y-3">
              {(() => {
                const percentage = (localConfig.wordCount - 10000) / (500000 - 10000);
                const trackWidth = percentage * 100;
                // 生成刻度标记，每5万一个，包含1万和50万
                const ticks = [];
                // 添加1万
                ticks.push({ value: 10000, position: 0, label: '1万' });
                // 添加5万到45万
                for (let i = 50000; i < 500000; i += 50000) {
                  const tickPercentage = (i - 10000) / (500000 - 10000) * 100;
                  ticks.push({ value: i, position: tickPercentage, label: (i / 10000).toFixed(0) + '万' });
                }
                // 添加50万
                ticks.push({ value: 500000, position: 100, label: '50万' });
                return (
                  <div className="relative">
                    {/* 滑动条容器 */}
                    <div className="relative h-6">
                      {/* 底层背景 - 渐变灰色 */}
                      <div className="absolute top-0 left-0 w-full h-6 bg-gradient-to-r from-gray-300 to-gray-100 rounded-full" />
                      {/* 已拖动区域背景 - 渐变蓝色（左浅右深） */}
                      <div 
                        className="absolute top-0 left-0 h-6 bg-gradient-to-r from-blue-300 to-blue-500 rounded-l-full"
                        style={{
                          width: `calc(${trackWidth}% - ${percentage * 24}px + 12px)`
                        }}
                      />
                      <input
                        type="range"
                        min="10000"
                        max="500000"
                        step="10000"
                        value={localConfig.wordCount}
                        onChange={(e) => updateConfig({ wordCount: parseInt(e.target.value) })}
                        className="absolute top-0 left-0 w-full h-6 rounded-full appearance-none cursor-pointer bg-transparent"
                        style={{
                          WebkitAppearance: 'none',
                        }}
                      />
                    </div>
                    {/* 刻度尺 */}
                    <div className="relative h-4 mt-1">
                      {ticks.map((tick) => {
                        // 最右侧刻度右对齐，最左侧刻度左对齐，其他居中
                        const isFirst = tick.position === 0;
                        const isLast = tick.position === 100;
                        const alignStyle = isLast 
                          ? { left: `${tick.position}%`, transform: 'translateX(-100%)' }
                          : isFirst
                          ? { left: `${tick.position}%`, transform: 'translateX(0)' }
                          : { left: `${tick.position}%`, transform: 'translateX(-50%)' };
                        
                        return (
                          <div
                            key={tick.value}
                            className="absolute top-0 flex flex-col items-center"
                            style={alignStyle}
                          >
                            <div className="w-px h-2 bg-gray-400" />
                            <span className="text-xs text-gray-500 mt-0.5 whitespace-nowrap">{tick.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              <style>{`
                input[type="range"]::-webkit-slider-thumb {
                  -webkit-appearance: none;
                  appearance: none;
                  width: 24px;
                  height: 24px;
                  border-radius: 50%;
                  background: #ffffff;
                  border: 3px solid #2563eb;
                  cursor: pointer;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                  margin-top: 0px;
                }
                input[type="range"]::-moz-range-thumb {
                  width: 24px;
                  height: 24px;
                  border-radius: 50%;
                  background: #ffffff;
                  border: 3px solid #2563eb;
                  cursor: pointer;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                }
                input[type="range"]::-webkit-slider-runnable-track {
                  height: 24px;
                  border-radius: 12px;
                  background: transparent;
                }
                input[type="range"]::-moz-range-track {
                  height: 24px;
                  border-radius: 12px;
                  background: transparent;
                }
              `}</style>
            </div>
          </div>

          {/* 项目类型 - 暂时隐藏，默认使用通用类 */}
          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              项目类型 <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              {PROJECT_TYPES.map((type, index) => {
                // 每个类型的默认颜色
                const defaultColors = [
                  { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', icon: '📋' },      // 通用类
                  { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: '📊' },      // 数据类
                  { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', icon: '⚙️' },   // 定开类
                  { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: '📦' }, // 产品类
                  { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: '🏍️' },    // 两轮车
                ];
                const colors = defaultColors[index] || defaultColors[0];
                const isSelected = localConfig.projectType === type;
                
                return (
                  <button
                    key={type}
                    onClick={() => updateConfig({ projectType: type })}
                    className={`relative flex-1 max-w-[140px] py-6 rounded-2xl border-2 transition-all duration-200 flex flex-col items-center justify-center gap-2 ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 shadow-lg scale-105'
                        : `${colors.bg} ${colors.border} hover:shadow-md hover:scale-102`
                    }`}
                  >
                    <span className="text-3xl">{colors.icon}</span>
                    <span className={`text-sm font-semibold ${
                      isSelected ? 'text-blue-700' : colors.text
                    }`}>
                      {type}
                    </span>
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div> */}
        </div>
      </div>

      {/* 文件上传区域 */}
      <div className="bg-white rounded-lg shadow p-6 mb-4">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">📄 文件上传</h2>
        
        <div className="space-y-6">
          {/* 招标文件上传 - 横向条形布局 */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
                <DocumentTextIcon className="w-4 h-4 text-blue-600" />
              </div>
              <h3 className="text-base font-medium text-gray-900">招标文件</h3>
              <span className="text-xs text-gray-400">只能上传一个，用于AI解析</span>
            </div>

            {!tenderFileInfo ? (
              /* 未上传状态 - 横向条 */
              <div 
                className="border-2 border-dashed border-blue-300 rounded-lg px-4 py-3 flex items-center space-x-3 hover:border-blue-500 hover:bg-blue-50/30 transition-all cursor-pointer group"
                onClick={() => tenderFileInputRef.current?.click()}
              >
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <CloudArrowUpIcon className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700">点击上传招标文件</p>
                  <p className="text-xs text-gray-500">支持 PDF、Word、TXT、MD 格式</p>
                </div>
                
                <input
                  ref={tenderFileInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.txt,.md"
                  onChange={handleTenderFileSelect}
                  className="hidden"
                />
              </div>
            ) : (
              /* 已上传状态 - 横向条 */
              (() => {
                const fileType = getFileTypeInfo(tenderFileInfo.name);
                const FileIcon = fileType.icon;
                return (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg px-4 py-3 border border-blue-200">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 ${fileType.bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <FileIcon className={`w-5 h-5 ${fileType.iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {tenderFileInfo.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(tenderFileInfo.size)} · {new Date(tenderFileInfo.uploadedAt).toLocaleString()}
                        </p>
                      </div>
                      <button
                        onClick={handleDeleteTenderFile}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="删除文件"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })()
            )}

            {uploadingTender && (
              <div className="flex items-center py-2">
                <div className="animate-spin h-4 w-4 text-blue-600 mr-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <span className="text-sm text-blue-600">正在上传招标文件...</span>
              </div>
            )}
          </div>

          {/* 分隔线 */}
          <div className="border-t border-gray-200"></div>

          {/* 其他资料上传 */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-green-100 rounded flex items-center justify-center">
                <FolderIcon className="w-4 h-4 text-green-600" />
              </div>
              <h3 className="text-base font-medium text-gray-900">其他资料</h3>
              <span className="text-xs text-gray-400">可上传多个文件，作为参考资料</span>
            </div>

            {/* 上传按钮 */}
            <div 
              className="border-2 border-dashed border-green-300 rounded-lg px-4 py-3 flex items-center space-x-3 hover:border-green-500 hover:bg-green-50/30 transition-all cursor-pointer group"
              onClick={() => otherFilesInputRef.current?.click()}
            >
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
                <PlusIcon className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-700">添加资料文件</p>
                <p className="text-xs text-gray-500">支持多种格式（文档、图片等）</p>
              </div>
              
              <input
                ref={otherFilesInputRef}
                type="file"
                accept=".pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.txt,.jpg,.jpeg,.png,.gif,.bmp,.webp"
                multiple
                onChange={handleOtherFilesSelect}
                className="hidden"
              />
            </div>

            {/* 已上传文件列表 */}
            {otherFilesInfo.length > 0 && (
              <div className="space-y-2">
                {otherFilesInfo.map((file, index) => {
                  const fileType = getFileTypeInfo(file.name);
                  const FileIcon = fileType.icon;
                  return (
                    <div 
                      key={file.id} 
                      className="flex items-center space-x-3 bg-gray-50 rounded-lg px-4 py-3 hover:bg-gray-100 transition-colors"
                    >
                      <div className={`w-8 h-8 ${fileType.bgColor} rounded flex items-center justify-center flex-shrink-0`}>
                        <FileIcon className={`w-4 h-4 ${fileType.iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDeleteOtherFile(index)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="删除文件"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {uploadingOther && (
              <div className="flex items-center py-2">
                <div className="animate-spin h-4 w-4 text-green-600 mr-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <span className="text-sm text-green-600">正在上传资料...</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 新建公司模态框 */}
      {showNewCompanyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">新建投标主体公司</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  公司简称 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newCompanyName}
                  onChange={(e) => setNewCompanyName(e.target.value)}
                  placeholder="例如：科技公司"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  公司全称 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newCompanyFullName}
                  onChange={(e) => setNewCompanyFullName(e.target.value)}
                  placeholder="例如：某某科技有限公司"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowNewCompanyModal(false);
                  setNewCompanyName('');
                  setNewCompanyFullName('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleAddCompany}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                确认添加
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 消息提示 */}
      {message && (
        <div className={`p-4 rounded-md ${
          message.type === 'success' 
            ? 'bg-green-100 text-green-700 border border-green-200' 
            : 'bg-red-100 text-red-700 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* 操作按钮 */}
      {onNext && (
        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
          <button
            onClick={() => navigate('/projects')}
            className="flex items-center px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            返回：新建标书
          </button>
          <button
            onClick={onNext}
            className="flex items-center px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            下一步：招标解析
            <ArrowRightIcon className="w-5 h-5 ml-2" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ProjectSetup;
